import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { fmtCVE } from "@/lib/format";
import { WORK_STATUS } from "@/pages/Works";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { WorkHours } from "@/components/WorkHours";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash, CircleNotch, FloppyDisk, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));
const token = () => localStorage.getItem("gestaocv_token");
const photoUrl = (fid) => `${API}/uploads/${fid}?auth=${token()}`;

function WorkBudget({ contract, spent }) {
  const profit = contract - spent;
  const pct = contract > 0 ? Math.min(100, Math.round((spent / contract) * 100)) : 0;
  const cells = [["Orçamento", contract, "work-budget-contract", ""], ["Gasto", spent, "work-budget-spent", ""],
    ["Lucro", profit, "work-budget-profit", profit < 0 ? "text-destructive" : "text-emerald-600"]];
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="work-budget">
      <div className="grid grid-cols-3 gap-3">
        {cells.map(([l, v, t, c]) => (
          <div key={t}><div className="text-xs text-muted-foreground">{l}</div><div className={`font-display text-lg font-extrabold ${c}`} data-testid={t}>{fmtCVE(v)}</div></div>
        ))}
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted"><div className={`h-full ${spent > contract ? "bg-destructive" : "bg-primary"}`} style={{ width: `${contract > 0 ? pct : 0}%` }} /></div>
      <p className="mt-1 text-xs text-muted-foreground">{contract > 0 ? `${Math.round((spent / contract) * 100)}% do orçamento gasto (materiais + despesas)` : "Indique o valor contratado para comparar"}</p>
    </div>
  );
}

export default function WorkDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const showHours = !!user?.features?.equipa;
  const navigate = useNavigate();
  const [w, setW] = useState(null);
  const [clients, setClients] = useState([]);
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const [wk, c, t] = await Promise.all([
      api.get(`/works/${id}`), api.get("/clients", { params: { limit: 200 } }), api.get("/team"),
    ]);
    setW(wk.data); setClients(c.data.items); setMembers(t.data.items);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setW((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: w.name, client_id: w.client_id || null, address: w.address, notes: w.notes,
        contract_value: Number(w.contract_value) || 0, status: w.status,
        start_date: w.start_date || null, end_date: w.end_date || null,
        completion: Number(w.completion) || 0, team: w.team || [],
        quote_id: w.quote_id || null, invoice_ids: w.invoice_ids || [],
        tasks: (w.tasks || []).map((t) => ({ ...t, done: !!t.done })),
        materials: (w.materials || []).filter((m) => (m.name || "").trim()).map((m) => ({ ...m, quantity: Number(m.quantity) || 0, cost: Number(m.cost) || 0 })),
        expenses: (w.expenses || []).filter((e) => (e.description || "").trim()).map((e) => ({ ...e, amount: Number(e.amount) || 0 })),
        photos: w.photos || [], progress_reports: w.progress_reports || [],
        time_entries: (w.time_entries || []).filter((t) => (t.worker_name || "").trim()).map((t) => ({ ...t, hours: Number(t.hours) || 0, rate: Number(t.rate) || 0 })),
      };
      const { data } = await api.put(`/works/${id}`, payload);
      setW(data); toast.success("Obra guardada");
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Eliminar esta obra?")) return;
    try { await api.delete(`/works/${id}`); toast.success("Obra eliminada"); navigate("/obras"); }
    catch (err) { toast.error(apiError(err)); }
  };

  const uploadPhoto = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (target === "photos") set("photos", [...(w.photos || []), { file_id: data.id, caption: "" }]);
      return data.id;
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); e.target.value = ""; }
  };

  if (!w) return <div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-primary" /></div>;

  const st = WORK_STATUS[w.status] || WORK_STATUS.planeada;
  const totalMaterials = (w.materials || []).reduce((s, m) => s + (Number(m.quantity) || 0) * (Number(m.cost) || 0), 0);
  const totalExpenses = (w.expenses || []).reduce((s, e) => s + (Number(e.amount) || 0), 0);

  return (
    <div className="space-y-5" data-testid="work-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" className="rounded-full" onClick={() => navigate("/obras")} data-testid="work-back"><ArrowLeft size={18} className="mr-1" /> Voltar</Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display text-2xl font-extrabold tracking-tight">{w.name}</h1>
              <Badge variant="outline" className={st.cls}>{st.label}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">{w.code} · {w.client_name || "Sem cliente"}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full text-destructive" onClick={remove} data-testid="delete-work-btn"><Trash size={16} className="mr-1.5" /> Eliminar</Button>
          <Button className="rounded-full" onClick={save} disabled={saving} data-testid="save-work-detail-btn">
            {saving ? <CircleNotch className="animate-spin" /> : <><FloppyDisk size={16} className="mr-1.5" /> Guardar</>}
          </Button>
        </div>
      </div>

      <WorkBudget contract={Number(w.contract_value) || 0} spent={totalMaterials + totalExpenses} />

      <Tabs defaultValue="detalhes">
        <TabsList className="flex w-full flex-wrap justify-start">
          <TabsTrigger value="detalhes" data-testid="tab-detalhes">Detalhes</TabsTrigger>
          <TabsTrigger value="tarefas" data-testid="tab-tarefas">Tarefas</TabsTrigger>
          <TabsTrigger value="materiais" data-testid="tab-materiais">Materiais</TabsTrigger>
          <TabsTrigger value="despesas" data-testid="tab-despesas">Despesas</TabsTrigger>
          {showHours && <TabsTrigger value="equipa" data-testid="tab-equipa">Equipa</TabsTrigger>}
          {showHours && <TabsTrigger value="horas" data-testid="tab-horas">Horas</TabsTrigger>}
          <TabsTrigger value="fotos" data-testid="tab-fotos">Fotos</TabsTrigger>
          <TabsTrigger value="relatorios" data-testid="tab-relatorios">Relatórios</TabsTrigger>
        </TabsList>

        <TabsContent value="detalhes" className="rounded-xl border border-border bg-card p-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Nome</Label><Input value={w.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Cliente</Label>
              <Select value={w.client_id || ""} onValueChange={(v) => set("client_id", v)}>
                <SelectTrigger><SelectValue placeholder="Sem cliente" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Endereço</Label><Input value={w.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Valor contratado (CVE)</Label><Input type="number" value={w.contract_value} onChange={(e) => set("contract_value", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Estado</Label>
              <Select value={w.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="work-status"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(WORK_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Início</Label><Input type="date" value={w.start_date || ""} onChange={(e) => set("start_date", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Fim previsto</Label><Input type="date" value={w.end_date || ""} onChange={(e) => set("end_date", e.target.value)} /></div>
            <div className="col-span-2 space-y-1.5">
              <Label>Conclusão: {Math.round(w.completion || 0)}%</Label>
              <input type="range" min="0" max="100" value={w.completion || 0} onChange={(e) => set("completion", e.target.value)}
                className="w-full accent-[hsl(var(--primary))]" data-testid="work-completion" />
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Notas</Label><Textarea value={w.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>
        </TabsContent>

        <TabsContent value="tarefas" className="rounded-xl border border-border bg-card p-5">
          <div className="space-y-2" data-testid="tasks-list">
            {(w.tasks || []).map((t, i) => (
              <div key={t.id || i} className="flex items-center gap-2 border-b border-border/60 pb-2">
                <Checkbox checked={!!t.done} onCheckedChange={(v) => { const a = [...w.tasks]; a[i] = { ...t, done: !!v }; set("tasks", a); }} />
                <Input className="h-8 flex-1" value={t.title} onChange={(e) => { const a = [...w.tasks]; a[i] = { ...t, title: e.target.value }; set("tasks", a); }} />
                <Select value={t.assignee_id || ""} onValueChange={(v) => { const a = [...w.tasks]; a[i] = { ...t, assignee_id: v }; set("tasks", a); }}>
                  <SelectTrigger className="h-8 w-40"><SelectValue placeholder="Responsável" /></SelectTrigger>
                  <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => set("tasks", w.tasks.filter((_, j) => j !== i))}><Trash size={15} /></Button>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" className="mt-3 rounded-full" data-testid="add-task-btn"
            onClick={() => set("tasks", [...(w.tasks || []), { id: uid(), title: "", done: false }])}><Plus size={15} className="mr-1" /> Adicionar tarefa</Button>
        </TabsContent>

        <TabsContent value="materiais" className="rounded-xl border border-border bg-card p-5">
          <div className="space-y-2">
            {(w.materials || []).map((m, i) => (
              <div key={m.id || i} className="grid grid-cols-12 items-center gap-2 border-b border-border/60 pb-2">
                <Input className="col-span-5 h-8" placeholder="Material" value={m.name} onChange={(e) => { const a = [...w.materials]; a[i] = { ...m, name: e.target.value }; set("materials", a); }} />
                <Input className="col-span-2 h-8" type="number" placeholder="Qtd" value={m.quantity} onChange={(e) => { const a = [...w.materials]; a[i] = { ...m, quantity: e.target.value }; set("materials", a); }} />
                <Input className="col-span-2 h-8" placeholder="Un" value={m.unit} onChange={(e) => { const a = [...w.materials]; a[i] = { ...m, unit: e.target.value }; set("materials", a); }} />
                <Input className="col-span-2 h-8" type="number" placeholder="Custo" value={m.cost} onChange={(e) => { const a = [...w.materials]; a[i] = { ...m, cost: e.target.value }; set("materials", a); }} />
                <Button variant="ghost" size="icon" className="col-span-1 h-8 w-8 text-destructive" onClick={() => set("materials", w.materials.filter((_, j) => j !== i))}><Trash size={15} /></Button>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between">
            <Button variant="outline" size="sm" className="rounded-full" data-testid="add-material-btn"
              onClick={() => set("materials", [...(w.materials || []), { id: uid(), name: "", quantity: 1, unit: "un", cost: 0 }])}><Plus size={15} className="mr-1" /> Adicionar material</Button>
            <span className="text-sm font-semibold">Total: {fmtCVE(totalMaterials)}</span>
          </div>
        </TabsContent>

        <TabsContent value="despesas" className="rounded-xl border border-border bg-card p-5">
          <div className="space-y-2">
            {(w.expenses || []).map((ex, i) => (
              <div key={ex.id || i} className="grid grid-cols-12 items-center gap-2 border-b border-border/60 pb-2">
                <Input className="col-span-6 h-8" placeholder="Descrição" value={ex.description} onChange={(e) => { const a = [...w.expenses]; a[i] = { ...ex, description: e.target.value }; set("expenses", a); }} />
                <Input className="col-span-3 h-8" type="date" value={ex.date || ""} onChange={(e) => { const a = [...w.expenses]; a[i] = { ...ex, date: e.target.value }; set("expenses", a); }} />
                <Input className="col-span-2 h-8" type="number" placeholder="Valor" value={ex.amount} onChange={(e) => { const a = [...w.expenses]; a[i] = { ...ex, amount: e.target.value }; set("expenses", a); }} />
                <Button variant="ghost" size="icon" className="col-span-1 h-8 w-8 text-destructive" onClick={() => set("expenses", w.expenses.filter((_, j) => j !== i))}><Trash size={15} /></Button>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between">
            <Button variant="outline" size="sm" className="rounded-full" data-testid="add-expense-btn"
              onClick={() => set("expenses", [...(w.expenses || []), { id: uid(), description: "", amount: 0, date: new Date().toISOString().slice(0, 10) }])}><Plus size={15} className="mr-1" /> Adicionar despesa</Button>
            <span className="text-sm font-semibold">Total: {fmtCVE(totalExpenses)}</span>
          </div>
        </TabsContent>

        <TabsContent value="equipa" className="rounded-xl border border-border bg-card p-5">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {members.map((m) => {
              const on = (w.team || []).includes(m.id);
              return (
                <label key={m.id} className="flex cursor-pointer items-center gap-3 rounded-lg border border-border p-3">
                  <Checkbox checked={on} onCheckedChange={(v) => set("team", v ? [...(w.team || []), m.id] : (w.team || []).filter((x) => x !== m.id))} />
                  <div><div className="text-sm font-medium">{m.name}</div><div className="text-xs text-muted-foreground">{m.email}</div></div>
                </label>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="horas" className="rounded-xl border border-border bg-card p-5">
          <WorkHours entries={w.time_entries} members={members} onChange={(v) => set("time_entries", v)} />
        </TabsContent>

        <TabsContent value="fotos" className="rounded-xl border border-border bg-card p-5">
          <label className="mb-4 inline-flex cursor-pointer items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-accent" data-testid="upload-photo-label">
            {uploading ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} />} Carregar foto
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e, "photos")} data-testid="upload-photo-input" />
          </label>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {(w.photos || []).map((p, i) => (
              <div key={p.file_id} className="group relative overflow-hidden rounded-lg border border-border">
                <img src={photoUrl(p.file_id)} alt={p.caption || "Foto da obra"} className="h-32 w-full object-cover" />
                <button onClick={() => set("photos", w.photos.filter((_, j) => j !== i))}
                  className="absolute right-1 top-1 rounded-full bg-black/60 p-1.5 text-white transition-colors hover:bg-red-600" data-testid={`delete-photo-${i}`}><Trash size={14} /></button>
              </div>
            ))}
            {(w.photos || []).length === 0 && <p className="text-sm text-muted-foreground">Sem fotografias.</p>}
          </div>
        </TabsContent>

        <TabsContent value="relatorios" className="rounded-xl border border-border bg-card p-5">
          <div className="space-y-3" data-testid="reports-list">
            {(w.progress_reports || []).map((r, i) => (
              <div key={r.id || i} className="rounded-lg border border-border p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-semibold">{r.date} · {Math.round(r.percent || 0)}%</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => set("progress_reports", w.progress_reports.filter((_, j) => j !== i))}><Trash size={14} /></Button>
                </div>
                <p className="text-sm text-muted-foreground">{r.note}</p>
              </div>
            ))}
          </div>
          <ReportAdder onAdd={(rep) => { set("progress_reports", [...(w.progress_reports || []), rep]); set("completion", rep.percent); }} />
        </TabsContent>
      </Tabs>
      <p className="text-center text-xs text-muted-foreground">Lembre-se de clicar em <b>Guardar</b> para gravar as alterações.</p>
    </div>
  );
}

function ReportAdder({ onAdd }) {
  const [percent, setPercent] = useState("");
  const [note, setNote] = useState("");
  const add = () => {
    if (!note.trim() && !percent) return;
    onAdd({ id: uid(), date: new Date().toISOString().slice(0, 10), percent: Number(percent) || 0, note, photo_ids: [] });
    setPercent(""); setNote("");
  };
  return (
    <div className="mt-4 space-y-2 rounded-lg border border-dashed border-border p-3">
      <div className="flex gap-2">
        <Input className="w-28" type="number" placeholder="% conclusão" value={percent} onChange={(e) => setPercent(e.target.value)} data-testid="report-percent" />
        <Input className="flex-1" placeholder="Descrição do progresso" value={note} onChange={(e) => setNote(e.target.value)} data-testid="report-note" />
        <Button variant="outline" className="rounded-full" onClick={add} data-testid="add-report-btn"><Plus size={15} className="mr-1" /> Adicionar</Button>
      </div>
    </div>
  );
}
